import { z } from 'zod';
import { IsoDate, Uuid } from './common.js';
import { Metric, PeriodQuery, withOrderedPeriod } from './analytics.js';
/**
 * Horevia Analytics (ADR-024) — analyse transverse hébergement + restauration +
 * bar + stock. Lecture seule ; module `analytics` requis.
 *
 * Deux conventions de jour cohabitent (R-ANA-02, A-079) : une vente POS
 * appartient à son **jour de service** (`orders.service_date`) ; paiements,
 * dépenses, mouvements de stock et nuits d'hébergement au jour civil du fuseau
 * de l'établissement.
 */
export const InsightsQuery = PeriodQuery.extend({
    /** Restreint les ventes POS à un point de vente (ignoré par l'hébergement). */
    outletId: Uuid.optional(),
});
export const InsightsQueryStrict = withOrderedPeriod(InsightsQuery);
/**
 * Famille de chiffre d'affaires de la vue consolidée : l'hébergement (séjours
 * hors folio) et une famille par nature de point de vente (`outlets.kind`).
 * Une commande imputée au séjour reste dans sa famille (R-ANA-01).
 */
export const RevenueModule = z.enum([
    'ACCOMMODATION',
    'RESTAURANT',
    'BAR',
    'ROOM_SERVICE',
    'SHOP',
    'OTHER',
]);
export const REVENUE_MODULES = RevenueModule.options;
export const RevenueByModuleRow = z.object({
    module: RevenueModule,
    revenue: Metric,
    /** Part du CA consolidé de la période, en pourcentage (0 → 100). */
    share: z.number(),
});
export const CollectedByMethodRow = z.object({
    method: z.string(),
    provider: z.string().nullable(),
    amount: z.number(),
    count: z.number().int(),
});
export const OutletRankingRow = z.object({
    outletId: Uuid,
    name: z.string(),
    kind: z.string(),
    revenue: z.number(),
    ordersCount: z.number().int(),
});
export const ProductRankingRow = z.object({
    productId: Uuid.nullable(),
    label: z.string(),
    quantity: z.number(),
    amount: z.number(),
});
const InsightsPeriod = z.object({ from: IsoDate, to: IsoDate, days: z.number().int() });
/** Vue consolidée (`GET /analytics/overview`, D-H56). */
export const AnalyticsOverview = z.object({
    period: InsightsPeriod,
    currencyCode: z.string().length(3),
    /** CA consolidé : chaque vente comptée une fois (R-ANA-01). */
    revenue: Metric,
    revenueByModule: z.array(RevenueByModuleRow),
    /** Série quotidienne : CA par famille (clés absentes = zéro). */
    daily: z.array(z.object({ date: IsoDate, revenue: z.record(RevenueModule, z.number()) })),
    topOutlets: z.array(OutletRankingRow),
    topProducts: z.array(ProductRankingRow),
    /** Volet hébergement — `null` sans le module `hotel`. */
    hotel: z
        .object({
        occupancyRate: Metric,
        averageDailyRate: Metric,
        revPar: Metric,
        staysCount: Metric,
    })
        .nullable(),
    /** Volet points de vente — `null` sans le module `pos`. */
    pos: z
        .object({
        ordersCount: Metric,
        coversCount: Metric,
        averageTicket: Metric,
        happyHourTotal: Metric,
        transferredCount: Metric,
        transferredTotal: Metric,
        cancelledCount: Metric,
    })
        .nullable(),
    /**
     * Volet financier — `null` sans `report.financial` (R-ANA-05). Coût des
     * ventes et marge valent `null` sans le module `inventory`.
     */
    finance: z
        .object({
        collected: Metric,
        collectedByMethod: z.array(CollectedByMethodRow),
        expenses: Metric,
        costOfSales: Metric.nullable(),
        grossMargin: Metric.nullable(),
        /** Marge brute ÷ CA HT des ventes POS, en pourcentage ; `null` si inconnu. */
        grossMarginRate: z.number().nullable(),
    })
        .nullable(),
});
/** Axes d'analyse des ventes POS (D-H57). */
export const SalesDimension = z.enum([
    'OUTLET',
    'CATEGORY',
    'PRODUCT',
    'HOUR',
    'WEEKDAY',
    'SERVER',
    'SERVICE_TYPE',
]);
export const SALES_DIMENSIONS = SalesDimension.options;
export const SalesBreakdownRow = z.object({
    /** Identifiant stable de la ligne (uuid, heure `0` → `23`, jour ISO `1` → `7`, code). */
    key: z.string(),
    label: z.string(),
    ordersCount: z.number().int(),
    /** Couverts — `null` sur les axes par article (catégorie, produit). */
    coversCount: z.number().int().nullable(),
    quantity: z.number(),
    revenue: z.number(),
    /** Part du CA des ventes de la période, en pourcentage. */
    share: z.number(),
    /** Ticket moyen — `null` sur les axes par article. */
    averageTicket: z.number().nullable(),
    happyHourTotal: z.number(),
});
/** Ventes POS (`GET /analytics/sales`, D-H57). */
export const SalesAnalytics = z.object({
    period: InsightsPeriod,
    currencyCode: z.string().length(3),
    totals: z.object({
        ordersCount: Metric,
        coversCount: Metric,
        revenue: Metric,
        averageTicket: Metric,
        happyHourTotal: Metric,
        transferredCount: Metric,
        transferredTotal: Metric,
        cancelledCount: Metric,
    }),
    byOutlet: z.array(SalesBreakdownRow),
    byCategory: z.array(SalesBreakdownRow),
    byProduct: z.array(SalesBreakdownRow),
    byHour: z.array(SalesBreakdownRow),
    byWeekday: z.array(SalesBreakdownRow),
    byServer: z.array(SalesBreakdownRow),
    byServiceType: z.array(SalesBreakdownRow),
    /** Encaissements des ventes par méthode — `null` sans `report.financial`. */
    byPaymentMethod: z.array(CollectedByMethodRow).nullable(),
});
export const StockConsumptionRow = z.object({
    itemId: Uuid,
    code: z.string(),
    name: z.string(),
    category: z.string(),
    unit: z.string(),
    consumedQuantity: z.number(),
    consumedCost: z.number(),
    purchasedQuantity: z.number(),
    purchasedCost: z.number(),
    lostQuantity: z.number(),
    lostCost: z.number(),
    countVarianceQuantity: z.number(),
    countVarianceCost: z.number(),
});
export const StockCategoryRow = z.object({
    category: z.string(),
    consumedCost: z.number(),
    purchasedCost: z.number(),
    lostCost: z.number(),
});
export const ProductMarginRow = z.object({
    productId: Uuid.nullable(),
    label: z.string(),
    quantity: z.number(),
    revenueExclTax: z.number(),
    /** Coût des ventes — `null` quand le produit n'a pas de recette (R-ANA-03). */
    cost: z.number().nullable(),
    margin: z.number().nullable(),
    marginRate: z.number().nullable(),
    costKnown: z.boolean(),
});
/** Stocks et marges (`GET /analytics/stock`, D-H58). */
export const StockAnalytics = z.object({
    period: InsightsPeriod,
    currencyCode: z.string().length(3),
    totals: z.object({
        consumedCost: Metric,
        purchasedCost: Metric,
        lostCost: Metric,
        countVarianceCost: Metric,
        /** Valeur du stock en fin de période (coût moyen pondéré). */
        stockValue: z.number(),
        /** CA HT des produits vendus dont le coût est connu. */
        revenueExclTax: Metric,
        grossMargin: Metric,
        grossMarginRate: z.number().nullable(),
    }),
    byItem: z.array(StockConsumptionRow),
    byCategory: z.array(StockCategoryRow),
    productMargins: z.array(ProductMarginRow),
});
/**
 * Module requis par rapport (R-ANA-04) : les rapports hôteliers exigent
 * `hotel`, les rapports d'analyse transverse `analytics` ; les rapports
 * financiers et clients sont communs à tous les tenants.
 */
export const REPORT_MODULES = {
    REVENUE: 'hotel',
    OCCUPANCY: 'hotel',
    RESERVATIONS: 'hotel',
    DISCOUNTS: 'hotel',
    RESOURCE_PERFORMANCE: 'hotel',
    SALES: 'analytics',
    PRODUCT_MARGIN: 'analytics',
    STOCK_CONSUMPTION: 'analytics',
    CONSOLIDATED_REVENUE: 'analytics',
    CUSTOMER_ACTIVITY: 'crm',
    LOYALTY: 'crm',
};
//# sourceMappingURL=insights.js.map